import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import ChatMessage from "@/components/chat/ChatMessage";
import ChatComposer from "@/components/chat/ChatComposer";

export default function ThreadPanel({ channelId, parentId, user, isAdmin, members, canPost = true, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const load = useCallback(() => {
    return api.get(`/chat/messages/${parentId}/thread`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [parentId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => { if (endRef.current) endRef.current.scrollTop = endRef.current.scrollHeight; }, [data]);

  const reply = async ({ body, mentions, attachments }) => {
    try {
      await api.post(`/chat/channels/${channelId}/messages`, { body, mentions, attachments, parent_id: parentId });
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };

  const react = async (m, emoji) => { try { await api.post(`/chat/messages/${m.id}/react`, { emoji }); load(); } catch {} };
  const pin = async (m) => { try { await api.post(`/chat/messages/${m.id}/pin`); load(); onChanged && onChanged(); } catch {} };
  const unpin = async (m) => { try { await api.delete(`/chat/messages/${m.id}/pin`); load(); onChanged && onChanged(); } catch {} };
  const edit = async (m, body) => { try { await api.patch(`/chat/messages/${m.id}`, { body }); load(); } catch (e) { toast.error("Modification impossible"); } };
  const del = async (m) => { try { await api.delete(`/chat/messages/${m.id}`); load(); onChanged && onChanged(); } catch {} };

  return (
    <div className="fixed inset-0 z-50 lg:static lg:inset-auto lg:z-auto w-full lg:w-[380px] border-l border-slate-200 bg-white flex flex-col lg:shrink-0" data-testid="thread-panel">
      <div className="px-4 py-3.5 border-b border-slate-200 flex items-center justify-between">
        <div className="font-semibold text-[#111111] text-sm">Fil de discussion</div>
        <button onClick={onClose} data-testid="thread-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
      </div>
      <div ref={endRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        ) : !data ? (
          <div className="text-sm text-slate-400 text-center py-8">Fil indisponible</div>
        ) : (
          <>
            <ChatMessage m={data.parent} user={user} isAdmin={isAdmin} members={members} canPost={canPost} inThread
              onReact={react} onPin={pin} onUnpin={unpin} onEdit={edit} onDelete={del} onOpenThread={() => {}} />
            <div className="border-t border-slate-100 pt-2 text-[10px] uppercase tracking-wider text-slate-400">
              {data.replies.length} réponse{data.replies.length > 1 ? "s" : ""}
            </div>
            {data.replies.map((r) => (
              <ChatMessage key={r.id} m={r} user={user} isAdmin={isAdmin} members={members} canPost={canPost} inThread
                onReact={react} onPin={pin} onUnpin={unpin} onEdit={edit} onDelete={del} onOpenThread={() => {}} />
            ))}
          </>
        )}
      </div>
      <ChatComposer members={members} onSend={reply} compact disabled={!canPost}
        placeholder={canPost ? "Répondre dans le fil…" : "Lecture seule"} />
    </div>
  );
}
